import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Box, Flex, Text } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { LuCircleCheck, LuExternalLink, LuLock, LuX } from 'react-icons/lu'
import { fonts, mineral, shadows } from '../../../theme'
import { ActionButton, Notice, ToneBadge } from '../../../components/analytics/ReportKit'
import { TONES } from '../../../components/analytics/reportTheme'
import type { SupplierRiskItem, SupplierRiskPenalty } from '../../../api/models/response/supplierRisk'
import { ScoreRing } from './ScoreMeter'
import {
  CLASS_ACTION,
  CLASS_LABEL,
  CLASS_RANGE,
  CLASS_TONE,
  RULE_COPY,
  fmtInt,
  isMissingVariant,
  penaltyTone,
  ruleShort,
  triggeredPenalties,
} from './riskCopy'

/**
 * Detalle de un proveedor: por qué tiene ese puntaje, regla por regla y en lenguaje
 * llano. Panel lateral propio (no el Drawer de Chakra) para no bloquear el scroll de
 * la página al montarse abierto.
 */
export function RiskDetailDrawer({ item, rank, onClose, onOpenProfile }: {
  item: SupplierRiskItem | null
  rank: number | null
  onClose: () => void
  /** Solo llega si el usuario puede ver la ficha del proveedor y hay companyId. */
  onOpenProfile?: (item: SupplierRiskItem) => void
}) {
  // Escape cierra el panel.
  useEffect(() => {
    if (!item) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [item, onClose])

  // Portal a <body>: el contenido de la página vive dentro de un contenedor animado
  // (con transform) y ahí `position: fixed` deja de ser relativo a la ventana.
  return createPortal(
    <AnimatePresence>
      {item && (
        <>
          <motion.div key="backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }} onClick={onClose}
            style={{ position: 'fixed', inset: 0, background: `${mineral.ink}52`, zIndex: 1400 }} />
          <motion.aside key="panel" role="dialog" aria-modal="true" aria-label={`Detalle de riesgo de ${item.legalName}`}
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 380, damping: 38 }}
            style={{
              position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(520px, 100vw)', zIndex: 1401,
              background: mineral.surface, boxShadow: shadows.lg, display: 'flex', flexDirection: 'column',
            }}>
            <DrawerBody item={item} rank={rank} onClose={onClose} onOpenProfile={onOpenProfile} />
          </motion.aside>
        </>
      )}
    </AnimatePresence>,
    document.body,
  )
}

function DrawerBody({ item, rank, onClose, onOpenProfile }: {
  item: SupplierRiskItem
  rank: number | null
  onClose: () => void
  onOpenProfile?: (item: SupplierRiskItem) => void
}) {
  const tone = CLASS_TONE[item.classification]
  const t = TONES[tone]
  const fired = triggeredPenalties(item)
  const criticals = fired.filter((p) => p.critical)
  const passed = item.penalties.filter((p) => p.dataAvailable && !p.triggered)
  const notEvaluated = item.penalties.filter((p) => !p.dataAvailable && !p.triggered)
  const rawSum = fired.reduce((acc, p) => acc + p.points, 0)

  return (
    <>
      {/* Cabecera */}
      <Flex align="flex-start" gap={3} px={5} py={4} borderBottom={`1px solid ${mineral.line}`}>
        <Box flex={1} minW={0}>
          <Text fontSize="11px" fontFamily={fonts.mono} color={mineral.ink3} letterSpacing="0.06em" textTransform="uppercase">
            {rank ? `Puesto ${fmtInt(rank)} del ranking` : 'Detalle de riesgo'}
          </Text>
          <Text fontFamily={fonts.display} fontWeight={800} fontSize="19px" color={mineral.ink} lineHeight="1.2" mt={0.5}>
            {item.legalName || '—'}
          </Text>
          <Text fontSize="12.5px" fontFamily={fonts.mono} color={mineral.ink3}>RUC {item.ruc || '—'}</Text>
        </Box>
        <Flex as="button" {...{ type: 'button', 'aria-label': 'Cerrar' }} onClick={onClose} w="34px" h="34px" borderRadius="10px"
          align="center" justify="center" color={mineral.ink3} cursor="pointer" flexShrink={0}
          _hover={{ bg: mineral.sunk, color: mineral.ink }}>
          <LuX size={18} />
        </Flex>
      </Flex>

      <Box flex={1} overflowY="auto" px={5} py={5}>
        {/* Puntaje */}
        <Flex align="center" gap={5} p={4} borderRadius="16px" bg={t.soft}>
          <ScoreRing score={item.score} tone={tone} />
          <Box minW={0}>
            <ToneBadge tone={tone}>Riesgo {CLASS_LABEL[item.classification].toLowerCase()}</ToneBadge>
            <Text fontFamily={fonts.display} fontWeight={700} fontSize="16px" color={t.deep} mt={2} lineHeight="1.25">
              {CLASS_ACTION[item.classification]}
            </Text>
            <Text fontSize="12px" color={mineral.ink2} mt={1}>
              Rango {CLASS_RANGE[item.classification]} puntos.
              {item.classification === 'High' && ' El bloqueo automático aún no está activo: hoy el ranking es una alerta.'}
            </Text>
          </Box>
        </Flex>

        {criticals.length > 0 && (
          <Box mt={4}>
            <Notice tone="bad">
              <Text as="span" fontWeight={700}>Tolerancia cero.</Text>{' '}
              {criticals.map((p) => ruleShort(p)).join(', ')}.{' '}
              {criticals.length === 1 ? 'Por sí sola deja' : 'Cada una por sí sola deja'} al proveedor en riesgo alto.
            </Notice>
          </Box>
        )}

        {/* Lo que sumó */}
        <SectionTitle>
          {fired.length === 0 ? 'No suma ningún punto' : `Por qué tiene ${fmtInt(item.score)} ${item.score === 1 ? 'punto' : 'puntos'}`}
        </SectionTitle>
        {fired.length === 0 ? (
          <Text fontSize="13px" color={mineral.ink2}>Cumple todas las reglas que hoy se pueden evaluar.</Text>
        ) : (
          <Flex direction="column" gap={2.5}>
            {fired.map((p, i) => <FiredRule key={p.code} penalty={p} index={i} />)}
            <Flex justify="space-between" align="center" px={1} pt={1} fontSize="12.5px" color={mineral.ink2}>
              <Text>Suma de las reglas</Text>
              <Text fontWeight={700} color={mineral.ink}>
                {fmtInt(rawSum)} pts{rawSum > 100 && ' · se limita a 100'}
              </Text>
            </Flex>
          </Flex>
        )}

        {/* Lo que cumple */}
        {passed.length > 0 && (
          <>
            <SectionTitle>Lo que está en regla</SectionTitle>
            <Flex direction="column" gap={1.5}>
              {passed.map((p) => (
                <Flex key={p.code} align="flex-start" gap={2.5}>
                  <Box color={TONES.ok.color} mt="2px" flexShrink={0}><LuCircleCheck size={15} /></Box>
                  <Box minW={0}>
                    <Text fontSize="13px" color={mineral.ink}>{p.label}</Text>
                    {p.detail && <Text fontSize="11.5px" color={mineral.ink3}>{p.detail}</Text>}
                  </Box>
                </Flex>
              ))}
            </Flex>
          </>
        )}

        {/* Lo que no se evalúa */}
        {notEvaluated.length > 0 && (
          <>
            <SectionTitle>No evaluado</SectionTitle>
            <Flex direction="column" gap={1.5}>
              {notEvaluated.map((p) => (
                <Flex key={p.code} align="flex-start" gap={2.5}>
                  <Box color={mineral.ink4} mt="2px" flexShrink={0}><LuLock size={14} /></Box>
                  <Box minW={0}>
                    <Text fontSize="13px" color={mineral.ink2}>
                      {p.label}
                      {p.premium && <Text as="span" fontSize="10px" fontWeight={700} color={TONES.accent.deep} ml={1.5}>PREMIUM</Text>}
                    </Text>
                    {p.detail && <Text fontSize="11.5px" color={mineral.ink3}>{p.detail}</Text>}
                  </Box>
                </Flex>
              ))}
            </Flex>
          </>
        )}
      </Box>

      {onOpenProfile && (
        <Flex px={5} py={3.5} borderTop={`1px solid ${mineral.line}`} justify="flex-end">
          <ActionButton icon={LuExternalLink} label="Ver ficha del proveedor" onClick={() => onOpenProfile(item)} />
        </Flex>
      )}
    </>
  )
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <Text fontSize="11px" fontFamily={fonts.mono} fontWeight={600} letterSpacing="0.08em" textTransform="uppercase"
      color={mineral.ink3} mt={6} mb={2.5}>
      {children}
    </Text>
  )
}

function FiredRule({ penalty: p, index }: { penalty: SupplierRiskPenalty; index: number }) {
  const t = TONES[penaltyTone(p)]
  const copy = RULE_COPY[p.code]
  const missing = isMissingVariant(p)
  return (
    <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * index + 0.1, duration: 0.25 }}>
      <Box border={`1px solid ${mineral.line}`} borderLeft={`3px solid ${t.color}`} borderRadius="12px" px={3.5} py={3}>
        <Flex align="flex-start" justify="space-between" gap={3}>
          <Box minW={0}>
            <Flex align="center" gap={2} wrap="wrap">
              <Text fontWeight={700} fontSize="13.5px" color={mineral.ink}>{ruleShort(p)}</Text>
              {p.critical && <ToneBadge tone="bad">Tolerancia cero</ToneBadge>}
            </Flex>
            {p.detail && <Text fontSize="12.5px" color={mineral.ink2} mt={0.5}>{p.detail}</Text>}
          </Box>
          <Text fontFamily={fonts.display} fontWeight={800} fontSize="15px" color={t.deep} whiteSpace="nowrap">
            +{fmtInt(p.points)}
          </Text>
        </Flex>
        {copy && (
          <Box mt={2} fontSize="12px" color={mineral.ink3}>
            <Text>{missing ? 'Sin este dato no se puede confirmar que el proveedor esté habilitado.' : copy.why}</Text>
            {copy.fix && <Text mt={0.5}><Text as="span" fontWeight={600} color={mineral.ink2}>Qué hacer:</Text> {copy.fix}</Text>}
          </Box>
        )}
      </Box>
    </motion.div>
  )
}
